import { useCallback } from 'react';
import { hideAppSpinner, showAppNotification, showAppSpinner } from '@rainbow-modules/app';
import { Customer } from 'data/firestore/agent/customer/types';
import { EntityGet } from 'data/firestore/types';
import useSMSChat from 'hooks/useSMSChat';
import { fetchInvoiceForOutput } from './api/client';
import { renderInvoicePdf } from './downloadInvoice';
import { buildInvoiceSmsText } from './helpers';
import { invoiceDownloadFileName } from './invoiceImages';
import { Invoice, InvoiceAcceptance } from './types';

interface SendInvoiceSmsInput {
    invoiceId: string;
    invoice?: Invoice;
    acceptance?: InvoiceAcceptance | null;
    customer?: EntityGet<Customer> | null;
    phone?: string;
}

const useSendInvoiceBySms = (organizationId: string) => {
    const { openChat } = useSMSChat();

    return useCallback(async ({
        invoiceId,
        invoice,
        acceptance,
        customer,
        phone,
    }: SendInvoiceSmsInput) => {
        const customerPhone = (phone || customer?.phoneNumber || '').trim();
        if (!customerPhone) {
            showAppNotification({
                title: 'Could not send invoice',
                description: 'This customer has no phone number.',
                icon: 'error',
                timeout: 5000,
            });
            return;
        }

        showAppSpinner();
        try {
            const loaded = invoice
                ? { invoice, acceptance: acceptance ?? null }
                : await fetchInvoiceForOutput(organizationId, invoiceId);
            const blob = await renderInvoicePdf(loaded.invoice, loaded.acceptance, organizationId);
            const file = new File(
                [blob],
                invoiceDownloadFileName(loaded.invoice),
                { type: 'application/pdf' },
            );
            openChat({
                ...(customer || {}),
                id: customer?.id || loaded.invoice.customer.id,
                displayName: customer?.displayName || loaded.invoice.customer.name,
                phoneNumber: customerPhone,
                smsDraft: {
                    text: buildInvoiceSmsText(loaded.invoice),
                    files: [file],
                },
            });
        } catch (error: any) {
            showAppNotification({
                title: 'Could not prepare invoice SMS',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [openChat, organizationId]);
};

export default useSendInvoiceBySms;
